import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// Types
interface User {
  email: string
  name: string
  role: string
  permissions: string[]
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

interface LoginResponse {
  success: boolean
  message: string
  user?: User
  access_token?: string
}

interface UserResponse {
  success: boolean
  user?: User
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Auth Provider Component
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user

  // Smart API base URL - environment aware (matches api.ts logic)
  const getApiBase = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL
    }
    
    if (process.env.NODE_ENV === 'production') {
      return '/api/v1'  // Use relative URL in production (same as api.ts)
    }
    
    // Development - use the same logic as api.ts
    const currentHost = window.location.hostname
    const currentPort = window.location.port
    
    if (currentPort === '3000') {
      return `http://${currentHost}:8002/api/v1`
    }
    
    return 'http://localhost:8002/api/v1'
  }
  
  const API_BASE = getApiBase()

  // Check authentication status on app load
  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data: UserResponse = await response.json()
        if (data.success && data.user) {
          setUser(data.user)
        } else {
          setUser(null)
        }
      } else {
        setUser(null)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true)

      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data: LoginResponse = await response.json()

      if (response.ok && data.success && data.user) {
        setUser(data.user)
        return true
      } else {
        console.error('Login failed:', data.message)
        return false
      }
    } catch (error) {
      console.error('Login error:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  // Logout function
  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
        },
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
    }
  }

  // Check auth on component mount
  useEffect(() => {
    checkAuth()
  }, [])

  // Auto-refresh token (optional, since we're using longer-lived tokens)
  useEffect(() => {
    if (isAuthenticated) {
      const refreshInterval = setInterval(async () => {
        try {
          await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          })
        } catch (error) {
          console.error('Token refresh failed:', error)
          // If refresh fails, check auth status
          checkAuth()
        }
      }, 30 * 60 * 1000) // Refresh every 30 minutes

      return () => clearInterval(refreshInterval)
    }
  }, [isAuthenticated])

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
